/**
 * EmailTemplateService (Embedded) - Professional HTML email template generation with ScanNinja branding
 * 
 * Provides self-contained email templates embedded directly in the service to avoid asset loading issues.
 * Templates include scan completion, security alerts, critical vulnerability notifications, and system maintenance.
 * 
 * Boundary: Does not send emails; only generates HTML/text content
 * External dependencies: Uses embedded HTML/CSS templates; no external asset loading
 */

import { Injectable } from '@angular/core';

export interface EmailTemplateData {
  // Common template variables
  logoUrl?: string;
  dashboardUrl?: string;
  settingsUrl?: string;
  supportUrl?: string;
  unsubscribeUrl?: string;

  // Template-specific variables
  [key: string]: any;
}

export interface EmailTemplate {
  name: string;
  subject: string;
  htmlContent: string;
  textContent?: string;
}

export interface ScanCompletionTemplateData extends EmailTemplateData {
  scanName: string;
  scanDuration: string;
  assetsScanned: number;
  completionTime: string;
  criticalCount: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
  hasVulnerabilities: boolean;
  resultsUrl: string;
}

export interface SecurityAlertTemplateData extends EmailTemplateData {
  alertTitle: string;
  alertDescription: string;
  severity: 'critical' | 'high' | 'medium';
  affectedSystems: string[];
  alertUrl: string;
}

export interface CriticalVulnerabilityTemplateData extends EmailTemplateData {
  vulnerabilityName: string;
  cveId: string;
  cvssScore: number;
  vulnerabilityDescription: string;
  affectedAssetsCount: number;
  riskLevel: string;
  exploitability: string;
  affectedAssets: Array<{
    name: string;
    ipAddress: string;
    service: string;
    status: string;
  }>;
  vulnDetailsUrl: string;
  remediationUrl: string;
  vulnerabilityUrl: string;
}

export interface SystemMaintenanceTemplateData extends EmailTemplateData {
  maintenanceTitle: string;
  maintenanceDescription: string;
  startTime: string;
  endTime: string;
  duration: string;
  timeZone: string;
  affectedServices: Array<{
    name: string;
    status: string;
  }>;
  statusPageUrl: string;
}

/**
 * Generates professional HTML email templates with embedded ScanNinja branding and styling.
 * All templates are self-contained to avoid asset loading issues in email clients.
 * 
 * Dependencies:
 * - None (no injected dependencies)
 * 
 * Side effects:
 * - None (pure template generation)
 */
@Injectable({
  providedIn: 'root',
})
export class EmailTemplateService {
  private readonly baseUrls = {
    logo: 'https://cdn.prod.website-files.com/67368f6c79297610e225f43b/67378b875c07c4139458e3ba_logo-top.png', // Use full URL for emails
    dashboard: 'https://your-domain.com/dashboard',
    settings: 'https://your-domain.com/my-account/notifications',
    support: 'https://your-domain.com/support',
    unsubscribe: 'https://your-domain.com/unsubscribe',
  };

  constructor() {}

  /**
   * Generates critical vulnerability alert email with urgency styling and remediation guidance
   * 
   * @param data - Critical vulnerability data including CVE, CVSS score, affected assets, exploitability
   * @returns EmailTemplate with red-themed HTML content, urgent subject line, and plain text version
   * - Observable semantics: N/A (synchronous method)
   * - HTTP errors: N/A
   * - State touched: None
   * - Idempotency: Safe to call multiple times with same data
   */
  getCriticalVulnerabilityTemplate(
    data: CriticalVulnerabilityTemplateData,
  ): EmailTemplate {
    const templateData = { ...this.getBaseTemplateData(), ...data };
    const htmlContent = this.processCriticalVulnerabilityTemplate(templateData);

    return {
      name: 'critical-vulnerability',
      subject: `🚨 CRITICAL: ${data.cveId} - ${data.vulnerabilityName}`,
      htmlContent,
      textContent: this.generateCriticalVulnerabilityTextVersion(data),
    };
  }

  /**
   * Generates security alert email with severity-based styling and affected systems list
   * 
   * @param data - Security alert data including title, description, severity (critical/high/medium), affected systems
   * @returns EmailTemplate with alert-themed HTML content, severity badge, and plain text version
   * - Observable semantics: N/A (synchronous method)
   * - HTTP errors: N/A
   * - State touched: None
   * - Idempotency: Safe to call multiple times with same data
   */
  getSecurityAlertTemplate(data: SecurityAlertTemplateData): EmailTemplate {
    const templateData = { ...this.getBaseTemplateData(), ...data };
    const htmlContent = this.processSecurityAlertTemplate(templateData);

    return {
      name: 'security-alert',
      subject: `🚨 SECURITY ALERT: ${data.alertTitle}`,
      htmlContent,
      textContent: this.generateSecurityAlertTextVersion(data),
    };
  }

  /**
   * Generates scan completion notification email with vulnerability statistics and result summary
   * 
   * @param data - Scan completion data including scan name, duration, vulnerability counts by severity
   * @returns EmailTemplate with green-themed HTML content for success, vulnerability breakdown grid
   * - Observable semantics: N/A (synchronous method)
   * - HTTP errors: N/A
   * - State touched: None
   * - Idempotency: Safe to call multiple times with same data
   */
  getScanCompletionTemplate(data: ScanCompletionTemplateData): EmailTemplate {
    const templateData = { ...this.getBaseTemplateData(), ...data };
    const htmlContent = this.processScanCompletionTemplate(templateData);

    return {
      name: 'scan-completion',
      subject: `Scan Complete: ${data.scanName}`,
      htmlContent,
      textContent: this.generateScanCompletionTextVersion(data),
    };
  }

  /**
   * Generates system maintenance notification email with schedule, duration, and affected services
   * 
   * @param data - Maintenance data including title, description, start/end times, affected services list
   * @returns EmailTemplate with blue-themed HTML content, schedule grid, and affected services table
   * - Observable semantics: N/A (synchronous method)
   * - HTTP errors: N/A
   * - State touched: None
   * - Idempotency: Safe to call multiple times with same data
   */
  getSystemMaintenanceTemplate(
    data: SystemMaintenanceTemplateData,
  ): EmailTemplate {
    const templateData = { ...this.getBaseTemplateData(), ...data };
    const htmlContent = this.processSystemMaintenanceTemplate(templateData);

    return {
      name: 'system-maintenance',
      subject: `Scheduled Maintenance: ${data.maintenanceTitle}`,
      htmlContent,
      textContent: this.generateSystemMaintenanceTextVersion(data),
    };
  }

  /**
   * Process critical vulnerability template
   */
  private processCriticalVulnerabilityTemplate(
    data: CriticalVulnerabilityTemplateData,
  ): string {
    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Critical Vulnerability - ScanNinja</title>
    <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; background: #f5f7fa; margin: 0; padding: 20px; }
        .container { max-width: 600px; margin: 0 auto; background: #fff; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
        .header { background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%); color: white; padding: 30px; text-align: center; }
        .logo { max-width: 150px; height: auto; margin-bottom: 15px; }
        .title { font-size: 24px; font-weight: 700; margin: 0; }
        .subtitle { font-size: 16px; margin: 8px 0 0 0; opacity: 0.9; }
        .content { padding: 30px; }
        .alert-banner { background: linear-gradient(45deg, #dc2626, #b91c1c); color: white; padding: 15px; text-align: center; margin: 20px 0; border-radius: 8px; font-weight: 600; text-transform: uppercase; }
        .cve-info { background: #fef2f2; border: 2px solid #dc2626; border-radius: 8px; padding: 20px; margin: 20px 0; }
        .cve-title { font-size: 18px; font-weight: 700; color: #7f1d1d; margin-bottom: 10px; }
        .cve-id { background: #7f1d1d; color: white; padding: 4px 8px; border-radius: 4px; font-family: monospace; font-size: 14px; }
        .cvss-score { margin: 15px 0; }
        .score-badge { background: #7f1d1d; color: white; padding: 8px 16px; border-radius: 20px; font-weight: 700; }
        .description { color: #991b1b; margin-top: 15px; }
        .impact-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin: 20px 0; }
        .impact-item { text-align: center; padding: 15px; background: #fff; border-radius: 6px; border: 1px solid #fbbf24; }
        .impact-value { font-size: 18px; font-weight: 700; color: #92400e; }
        .impact-label { font-size: 12px; color: #a16207; text-transform: uppercase; margin-top: 5px; }
        .btn { display: inline-block; padding: 12px 24px; background: #dc2626; color: white; text-decoration: none; border-radius: 6px; font-weight: 600; margin: 10px 5px; }
        .btn-primary { background: #667eea; }
        .footer { background: #f3f4f6; padding: 20px; text-align: center; color: #6b7280; font-size: 14px; }
        .footer a { color: #667eea; margin: 0 10px; }
        @media (max-width: 600px) { .impact-grid { grid-template-columns: 1fr; } .content { padding: 20px; } }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <img src="${data.logoUrl}" alt="ScanNinja Security" class="logo">
            <h1 class="title">Critical Vulnerability Detected</h1>
            <p class="subtitle">Immediate remediation required</p>
        </div>

        <div class="content">
            <div class="alert-banner">
                🚨 Critical Security Risk - Action Required Within 24 Hours 🚨
            </div>

            <div class="cve-info">
                <div class="cve-title">${data.vulnerabilityName}</div>
                <span class="cve-id">${data.cveId}</span>
                <div class="cvss-score">
                    <span class="score-badge">CVSS ${data.cvssScore}/10</span>
                </div>
                <div class="description">${data.vulnerabilityDescription}</div>
            </div>

            <div class="impact-grid">
                <div class="impact-item">
                    <div class="impact-value">${data.affectedAssetsCount}</div>
                    <div class="impact-label">Affected Assets</div>
                </div>
                <div class="impact-item">
                    <div class="impact-value">${data.riskLevel}</div>
                    <div class="impact-label">Risk Level</div>
                </div>
                <div class="impact-item">
                    <div class="impact-value">${data.exploitability}</div>
                    <div class="impact-label">Exploitability</div>
                </div>
            </div>

            <div style="text-align: center; margin: 30px 0;">
                <a href="${data.vulnDetailsUrl}" class="btn">View Vulnerability Details</a>
                <a href="${data.remediationUrl}" class="btn btn-primary">Remediation Guide</a>
            </div>
        </div>

        <div class="footer">
            <p>This critical vulnerability alert was sent by ScanNinja Security Platform</p>
            <div>
                <a href="${data.dashboardUrl}">Dashboard</a>
                <a href="${data.vulnerabilityUrl}">Vulnerability Management</a>
                <a href="${data.supportUrl}">Emergency Support</a>
            </div>
        </div>
    </div>
</body>
</html>`.trim();
  }

  /**
   * Process security alert template
   */
  private processSecurityAlertTemplate(
    data: SecurityAlertTemplateData,
  ): string {
    const affectedSystemsList = data.affectedSystems
      .map((system) => `<li>${system}</li>`)
      .join('');

    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Security Alert - ScanNinja</title>
    <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; background: #f5f7fa; margin: 0; padding: 20px; }
        .container { max-width: 600px; margin: 0 auto; background: #fff; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
        .header { background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); color: white; padding: 30px; text-align: center; }
        .alert-icon { font-size: 40px; margin-bottom: 15px; }
        .logo { max-width: 150px; height: auto; margin-bottom: 15px; }
        .title { font-size: 26px; font-weight: 700; margin: 0; }
        .subtitle { font-size: 16px; margin: 8px 0 0 0; opacity: 0.9; }
        .content { padding: 30px; }
        .alert-summary { background: #fef2f2; border: 2px solid #fca5a5; border-radius: 8px; padding: 20px; margin: 20px 0; }
        .alert-title { font-size: 18px; font-weight: 700; color: #7f1d1d; margin-bottom: 15px; }
        .severity-badge { display: inline-block; padding: 6px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; text-transform: uppercase; margin: 10px 0; }
        .severity-critical { background: #7f1d1d; color: white; }
        .severity-high { background: #dc2626; color: white; }
        .severity-medium { background: #f59e0b; color: white; }
        .alert-details { color: #991b1b; }
        .urgency-message { background: #fef2f2; border: 2px solid #dc2626; border-radius: 8px; padding: 20px; margin: 20px 0; text-align: center; }
        .affected-systems { background: #f9fafb; border-radius: 8px; padding: 20px; margin: 20px 0; }
        .systems-title { font-size: 16px; font-weight: 600; color: #1f2937; margin-bottom: 15px; }
        .systems-list { list-style: none; padding: 0; }
        .systems-list li { padding: 8px 0; border-bottom: 1px solid #e5e7eb; color: #4b5563; }
        .btn { display: inline-block; padding: 12px 24px; background: #dc2626; color: white; text-decoration: none; border-radius: 6px; font-weight: 600; margin: 10px 5px; }
        .btn-primary { background: #667eea; }
        .footer { background: #f3f4f6; padding: 20px; text-align: center; color: #6b7280; font-size: 14px; }
        .footer a { color: #667eea; margin: 0 10px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <div class="alert-icon">🚨</div>
            <img src="${data.logoUrl}" alt="ScanNinja Security" class="logo">
            <h1 class="title">Security Alert</h1>
            <p class="subtitle">Immediate attention required</p>
        </div>

        <div class="content">
            <div class="alert-summary">
                <div class="alert-title">⚠️ ${data.alertTitle}</div>
                <div class="severity-badge severity-${data.severity}">${data.severity} Severity</div>
                <div class="alert-details">${data.alertDescription}</div>
            </div>

            <div class="urgency-message">
                <p style="font-weight: 600; color: #7f1d1d; margin: 0;">
                    This alert requires immediate attention to prevent potential security compromise.
                </p>
            </div>

            <div class="affected-systems">
                <div class="systems-title">Affected Systems</div>
                <ul class="systems-list">
                    ${affectedSystemsList}
                </ul>
            </div>

            <div style="text-align: center; margin: 30px 0;">
                <a href="${data.alertUrl}" class="btn">View Alert Details</a>
                <a href="${data.dashboardUrl}" class="btn btn-primary">Go to Dashboard</a>
            </div>

            <p style="text-align: center; color: #6b7280; font-size: 14px; margin-top: 20px;">
                For immediate assistance, contact your security team or call our 24/7 support line.
            </p>
        </div>

        <div class="footer">
            <p>This security alert was sent by ScanNinja Security Platform</p>
            <div>
                <a href="${data.dashboardUrl}">Dashboard</a>
                <a href="${data.settingsUrl}">Alert Settings</a>
                <a href="${data.supportUrl}">Emergency Support</a>
            </div>
        </div>
    </div>
</body>
</html>`.trim();
  }

  /**
   * Process scan completion template
   */
  private processScanCompletionTemplate(
    data: ScanCompletionTemplateData,
  ): string {
    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Scan Complete - ScanNinja</title>
    <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; background: #f5f7fa; margin: 0; padding: 20px; }
        .container { max-width: 600px; margin: 0 auto; background: #fff; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
        .header { background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white; padding: 30px; text-align: center; }
        .logo { max-width: 150px; height: auto; margin-bottom: 15px; }
        .title { font-size: 24px; font-weight: 600; margin: 0; }
        .subtitle { font-size: 16px; margin: 8px 0 0 0; opacity: 0.9; }
        .content { padding: 30px; }
        .scan-summary { background: #f9fafb; border-radius: 8px; padding: 20px; margin: 20px 0; border-left: 5px solid #10b981; }
        .scan-name { font-size: 18px; font-weight: 600; color: #1f2937; margin-bottom: 10px; }
        .scan-details { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin: 15px 0; }
        .detail-item { text-align: center; }
        .detail-label { font-size: 14px; color: #6b7280; margin-bottom: 5px; }
        .detail-value { font-size: 16px; font-weight: 600; color: #1f2937; }
        .vuln-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin: 20px 0; }
        .vuln-stat { text-align: center; padding: 15px; border-radius: 8px; border: 2px solid; }
        .vuln-critical { background: #fef2f2; border-color: #dc2626; color: #7f1d1d; }
        .vuln-high { background: #fef3c7; border-color: #f59e0b; color: #92400e; }
        .vuln-medium { background: #fef7ff; border-color: #8b5cf6; color: #581c87; }
        .vuln-low { background: #f0f9ff; border-color: #3b82f6; color: #1e40af; }
        .vuln-count { font-size: 20px; font-weight: 700; display: block; }
        .vuln-label { font-size: 12px; font-weight: 500; text-transform: uppercase; margin-top: 5px; }
        .btn { display: inline-block; padding: 12px 24px; background: #10b981; color: white; text-decoration: none; border-radius: 6px; font-weight: 600; margin: 10px 5px; }
        .btn-primary { background: #667eea; }
        .footer { background: #f3f4f6; padding: 20px; text-align: center; color: #6b7280; font-size: 14px; }
        .footer a { color: #667eea; margin: 0 10px; }
        @media (max-width: 600px) { .scan-details, .vuln-stats { grid-template-columns: repeat(2, 1fr); } .content { padding: 20px; } }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <img src="${data.logoUrl}" alt="ScanNinja Security" class="logo">
            <h1 class="title">Scan Complete</h1>
            <p class="subtitle">${data.scanName} has finished scanning</p>
        </div>

        <div class="content">
            <div class="scan-summary">
                <div class="scan-name">${data.scanName}</div>
                <div class="scan-details">
                    <div class="detail-item">
                        <div class="detail-label">Duration</div>
                        <div class="detail-value">${data.scanDuration}</div>
                    </div>
                    <div class="detail-item">
                        <div class="detail-label">Assets Scanned</div>
                        <div class="detail-value">${data.assetsScanned}</div>
                    </div>
                    <div class="detail-item">
                        <div class="detail-label">Completed</div>
                        <div class="detail-value">${data.completionTime}</div>
                    </div>
                </div>
            </div>

            <div class="vuln-stats">
                <div class="vuln-stat vuln-critical">
                    <span class="vuln-count">${data.criticalCount}</span>
                    <span class="vuln-label">Critical</span>
                </div>
                <div class="vuln-stat vuln-high">
                    <span class="vuln-count">${data.highCount}</span>
                    <span class="vuln-label">High</span>
                </div>
                <div class="vuln-stat vuln-medium">
                    <span class="vuln-count">${data.mediumCount}</span>
                    <span class="vuln-label">Medium</span>
                </div>
                <div class="vuln-stat vuln-low">
                    <span class="vuln-count">${data.lowCount}</span>
                    <span class="vuln-label">Low</span>
                </div>
            </div>

            <p style="margin: 20px 0; color: #555;">
                ${
                  data.hasVulnerabilities
                    ? 'Your scan has identified vulnerabilities that require attention. Please review the detailed results and take appropriate action for critical and high-severity issues.'
                    : 'Great news! No vulnerabilities were detected in this scan. Your systems appear to be secure based on the current scan parameters.'
                }
            </p>

            <div style="text-align: center; margin: 30px 0;">
                <a href="${data.resultsUrl}" class="btn btn-primary">View Detailed Results</a>
                <a href="${data.dashboardUrl}" class="btn">Go to Dashboard</a>
            </div>
        </div>

        <div class="footer">
            <p>This email was sent by ScanNinja Security Platform</p>
            <div>
                <a href="${data.dashboardUrl}">Dashboard</a>
                <a href="${data.settingsUrl}">Notification Settings</a>
                <a href="${data.supportUrl}">Support</a>
                <a href="${data.unsubscribeUrl}">Unsubscribe</a>
            </div>
        </div>
    </div>
</body>
</html>`.trim();
  }

  /**
   * Process system maintenance template
   */
  private processSystemMaintenanceTemplate(
    data: SystemMaintenanceTemplateData,
  ): string {
    const servicesList = data.affectedServices
      .map(
        (service) =>
          `<div style="background: #fff; padding: 15px; border-radius: 6px; text-align: center; border: 1px solid #e5e7eb; margin: 5px;">
        <div style="font-weight: 600; color: #1f2937; margin-bottom: 5px;">${service.name}</div>
        <div style="font-size: 12px; color: #6b7280;">${service.status}</div>
      </div>`,
      )
      .join('');

    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>System Maintenance - ScanNinja</title>
    <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; background: #f5f7fa; margin: 0; padding: 20px; }
        .container { max-width: 600px; margin: 0 auto; background: #fff; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
        .header { background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); color: white; padding: 30px; text-align: center; }
        .logo { max-width: 150px; height: auto; margin-bottom: 15px; }
        .title { font-size: 24px; font-weight: 600; margin: 0; }
        .subtitle { font-size: 16px; margin: 8px 0 0 0; opacity: 0.9; }
        .content { padding: 30px; }
        .maintenance-info { background: #f0f9ff; border: 2px solid #93c5fd; border-radius: 8px; padding: 20px; margin: 20px 0; }
        .maintenance-title { font-size: 18px; font-weight: 600; color: #1e40af; margin-bottom: 15px; }
        .schedule-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin: 20px 0; }
        .schedule-item { background: #f9fafb; padding: 15px; border-radius: 8px; text-align: center; border: 1px solid #e5e7eb; }
        .schedule-label { font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; font-weight: 500; }
        .schedule-value { font-size: 16px; font-weight: 600; color: #1f2937; }
        .services-section { background: #f9fafb; border-radius: 8px; padding: 20px; margin: 20px 0; }
        .services-title { font-size: 16px; font-weight: 600; color: #1f2937; margin-bottom: 15px; }
        .services-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
        .btn { display: inline-block; padding: 12px 24px; background: #3b82f6; color: white; text-decoration: none; border-radius: 6px; font-weight: 600; margin: 10px 5px; }
        .btn-primary { background: #667eea; }
        .footer { background: #f3f4f6; padding: 20px; text-align: center; color: #6b7280; font-size: 14px; }
        .footer a { color: #667eea; margin: 0 10px; }
        @media (max-width: 600px) { .schedule-grid, .services-grid { grid-template-columns: 1fr; } .content { padding: 20px; } }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <img src="${data.logoUrl}" alt="ScanNinja Security" class="logo">
            <h1 class="title">Scheduled Maintenance</h1>
            <p class="subtitle">System updates and improvements</p>
        </div>

        <div class="content">
            <div class="maintenance-info">
                <div class="maintenance-title">🔧 ${data.maintenanceTitle}</div>
                <div style="color: #1e40af;">${data.maintenanceDescription}</div>
            </div>

            <div class="schedule-grid">
                <div class="schedule-item">
                    <div class="schedule-label">Start Time</div>
                    <div class="schedule-value">${data.startTime}</div>
                </div>
                <div class="schedule-item">
                    <div class="schedule-label">End Time</div>
                    <div class="schedule-value">${data.endTime}</div>
                </div>
                <div class="schedule-item">
                    <div class="schedule-label">Duration</div>
                    <div class="schedule-value">${data.duration}</div>
                </div>
                <div class="schedule-item">
                    <div class="schedule-label">Time Zone</div>
                    <div class="schedule-value">${data.timeZone}</div>
                </div>
            </div>

            <div class="services-section">
                <div class="services-title">Affected Services</div>
                <div class="services-grid">
                    ${servicesList}
                </div>
            </div>

            <div style="text-align: center; margin: 30px 0;">
                <a href="${data.statusPageUrl}" class="btn">View Status Page</a>
                <a href="${data.supportUrl}" class="btn btn-primary">Contact Support</a>
            </div>
        </div>

        <div class="footer">
            <p>This maintenance notification was sent by ScanNinja Security Platform</p>
            <div>
                <a href="${data.dashboardUrl}">Dashboard</a>
                <a href="${data.statusPageUrl}">Status Page</a>
                <a href="${data.supportUrl}">Support</a>
                <a href="${data.settingsUrl}">Notification Settings</a>
            </div>
        </div>
    </div>
</body>
</html>`.trim();
  }

  /**
   * Constructs base template data with common URLs for logo, dashboard, settings, support, unsubscribe
   * 
   * @returns EmailTemplateData with populated base URL properties
   */
  private getBaseTemplateData(): EmailTemplateData {
    return {
      logoUrl: this.baseUrls.logo,
      dashboardUrl: this.baseUrls.dashboard,
      settingsUrl: this.baseUrls.settings,
      supportUrl: this.baseUrls.support,
      unsubscribeUrl: this.baseUrls.unsubscribe,
    };
  }

  /**
   * Generate text versions for accessibility
   */
  private generateCriticalVulnerabilityTextVersion(
    data: CriticalVulnerabilityTemplateData,
  ): string {
    return `
🚨 CRITICAL VULNERABILITY DETECTED - ScanNinja Security

${data.vulnerabilityName}
CVE ID: ${data.cveId}
CVSS Score: ${data.cvssScore}/10

${data.vulnerabilityDescription}

Impact:
- Affected Assets: ${data.affectedAssetsCount}
- Risk Level: ${data.riskLevel}
- Exploitability: ${data.exploitability}

IMMEDIATE ACTION REQUIRED WITHIN 24 HOURS

View Details: ${data.vulnDetailsUrl}
Remediation Guide: ${data.remediationUrl}

ScanNinja Security Platform
    `.trim();
  }

  private generateSecurityAlertTextVersion(
    data: SecurityAlertTemplateData,
  ): string {
    return `
🚨 SECURITY ALERT - ScanNinja Security

${data.alertTitle}
Severity: ${data.severity.toUpperCase()}

${data.alertDescription}

Affected Systems:
${data.affectedSystems.map((system) => `- ${system}`).join('\n')}

This alert requires immediate attention to prevent potential security compromise.

View Alert: ${data.alertUrl}
Dashboard: ${data.dashboardUrl}

ScanNinja Security Platform
    `.trim();
  }

  private generateScanCompletionTextVersion(
    data: ScanCompletionTemplateData,
  ): string {
    return `
ScanNinja Security - Scan Complete

Scan: ${data.scanName}
Duration: ${data.scanDuration}
Assets Scanned: ${data.assetsScanned}
Completed: ${data.completionTime}

Vulnerabilities Found:
- Critical: ${data.criticalCount}
- High: ${data.highCount}
- Medium: ${data.mediumCount}
- Low: ${data.lowCount}

${
  data.hasVulnerabilities
    ? 'Your scan has identified vulnerabilities that require attention.'
    : 'Great news! No vulnerabilities were detected in this scan.'
}

View Results: ${data.resultsUrl}
Dashboard: ${data.dashboardUrl}

ScanNinja Security Platform
    `.trim();
  }

  private generateSystemMaintenanceTextVersion(
    data: SystemMaintenanceTemplateData,
  ): string {
    return `
SCHEDULED MAINTENANCE - ScanNinja Security

${data.maintenanceTitle}
${data.maintenanceDescription}

Schedule:
- Start: ${data.startTime}
- End: ${data.endTime}
- Duration: ${data.duration}
- Time Zone: ${data.timeZone}

Status Page: ${data.statusPageUrl}
Support: ${data.supportUrl}

ScanNinja Security Platform
    `.trim();
  }
}
